/**
 * Only relative CMS paths are re-pointed at the asset host.
 *
 * The inherited B2C rule also rewrote any absolute URL on a `*nostra*` host to
 * the asset base. On B2B that breaks: the CMS stores fully-qualified URLs on
 * hosts that are not the asset host (e.g. banner artwork on
 * nostra.multishop.lt), and rewriting them points at a host where the file does
 * not exist. An absolute URL already says where the file lives — leave it be.
 */
export function shouldProxyImage(src: string): boolean {
    if (src.startsWith('http://') || src.startsWith('https://')) return false
    return !src.startsWith('/img')
}

export function imageCDN(src: string | undefined): string {
    if (!src) return ''
    if (!shouldProxyImage(src)) return src
    const config = useRuntimeConfig()
    const assetBaseUrl = (config.public.assetBaseUrl as string).replace(/\/$/, '')
    const cb = extractCacheBuster(src)
    const url = `${assetBaseUrl}/${resolveImageSrc(src)}`
    return cb ? withCacheBuster(url, cb) : url
}

/** Appends a cache-busting `cb` query param to a src string, if a value is provided. */
export function withCacheBuster(src: string, cacheBuster?: number | string | null): string {
    if (!cacheBuster) return src
    const sep = src.includes('?') ? '&' : '?'
    return `${src}${sep}cb=${cacheBuster}`
}

/** Reads the `cb` query param back off a src string (survives resolveImageSrc's stripping). */
export function extractCacheBuster(src: string): string | undefined {
    try {
        const query = src.includes('?') ? src.split('?')[1] : (new URL(src).search.slice(1))
        return new URLSearchParams(query).get('cb') ?? undefined
    } catch { return undefined }
}

/** Returns normalized relative path (no leading slash, legacy prefix collapsed). */
export function resolveImageSrc(src: string): string {
    let path: string
    if (src.startsWith('http://') || src.startsWith('https://')) {
        // Full URL: extract only the pathname, drop query params
        path = new URL(src).pathname
    } else {
        // Relative URL: strip query params
        path = src.split('?')[0]!
    }
    // Normalize: strip leading slash, collapse legacy CMS upload prefix
    return path.replace(/^\//, '').replace('/res/tr_images/', '/')
}
